import { useState, useEffect } from "react";
import {
    Form,
    Input,
    Radio,
    Select,
    Button,
    Row,
    Col,
    Switch,
    Upload,
    message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
    createClient,
    updateClient,
    deleteClient,
    getClientGroups,
    getClientSubgroups,
    getAvailableChannels,
    getAvailablePlans,
    sendRequest,
    uploadClientLogo,
} from "@/ts/retrieve-data";
import { showToast } from "@/ts/modals";
import {
    IClientGroup,
    IClientSubgroup,
    ICreateClientPayload,
    IPlan,
    CustomOptionType,
    ParentModule,
    ClientFormFields,
    IClientContact,
    IChannels,
} from "@/ts/common-types";
import { Popconfirm } from "antd";
import { fetchCountriesData, fetchTimezones } from "@/ts/utils";
import { useNavigate } from "react-router-dom";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";
import * as Sentry from "@sentry/react";

interface ClientFormProps {
    editData: any | null;
    metadata?: {
        groups: IClientGroup[];
        subgroups: IClientSubgroup[];
    };
    onSuccess: () => void;
    onCancel: () => void;
}

const { Option } = Select;
interface ICountry {
    name: string;
    alpha2_code: string;
    country_locale: string[];
    currency_code: string;
    currency_name: string;
}

interface ITimezone {
    abbrev: string;
    utc_offset: {
        hours: number;
        minutes?: number;
    };
    is_dst: boolean;
    label: string;
    value: string;
}

interface IClientFormValues {
    [ClientFormFields.Name]: string;
    [ClientFormFields.Country]: string;
    [ClientFormFields.Timezone]: string;
    [ClientFormFields.Domain]?: string;
    [ClientFormFields.Status]?: boolean;
    [ClientFormFields.Contact]?: IClientContact;
    [ClientFormFields.ClientGroupId]: number;
    [ClientFormFields.ClientGroupName]: string;
    [ClientFormFields.ClientSubgroupId]: number | "new";
    [ClientFormFields.ClientSubgroupName]: string;
    [ClientFormFields.PlanId]: number;
    [ClientFormFields.IsNewGroup]: boolean;
    [ClientFormFields.Logo]?: File | null;
}

export const ClientForm = ({
    editData,
    metadata,
    onSuccess,
    onCancel,
}: ClientFormProps) => {
    const [form] = Form.useForm();
    const [isNewGroup, setIsNewGroup] = useState(false);
    const [clientGroups, setClientGroups] = useState<IClientGroup[]>([]);
    const [subgroups, setSubgroups] = useState<IClientSubgroup[]>([]);
    const [channels, setChannels] = useState<IChannels[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
    const [countries, setCountries] = useState<ICountry[]>([]);
    const [timezones, setTimezones] = useState<ITimezone[]>([]);
    const [plans, setPlans] = useState<IPlan[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>("");
    const [isNewSubgroup, setIsNewSubgroup] = useState(false);
    const navigate = useNavigate();
    const { canUpdateClient, canDeleteClient } = usePermissionCheck();
    const [clientStatus, setClientStatus] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    const fetchLogoWithAuth = async (filename: string): Promise<string> => {
        const response: Response = await sendRequest(
            {
                type: `clients/logo/${filename}`,
                method: "GET",
            },
            true,
            false,
            true
        );

        if (!response.ok) {
            throw new Error(
                `Failed to fetch logo: ${response.status} ${response.statusText}`
            );
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    };

    const isCreating = !editData;

    useEffect(() => {
        const initializeForm = async () => {
            await loadInitialData();

            if (editData) {
                if (editData.client_group.id) {
                    await handleGroupChange(editData.client_group.id);
                }

                form.setFieldsValue({
                    [ClientFormFields.Name]: editData.name,
                    [ClientFormFields.Country]: editData.country,
                    [ClientFormFields.Timezone]: editData.timezones?.[0] || "",
                    [ClientFormFields.Domain]: editData.domain,
                    [ClientFormFields.Status]: editData.status,
                    [ClientFormFields.Contact]: editData.contact,
                    [ClientFormFields.ClientGroupId]: editData.client_group.id,
                    [ClientFormFields.ClientGroupName]:
                        editData.client_group.name,
                    [ClientFormFields.ClientSubgroupId]:
                        editData.client_subgroup.id,
                    [ClientFormFields.ClientSubgroupName]:
                        editData.client_subgroup.name,
                    [ClientFormFields.SelectedChannels]: editData.channels,
                });

                if (editData.logo_url) {
                    const filename = editData.logo_url.split("/").pop();
                    if (filename) {
                        try {
                            const logoBlobUrl = await fetchLogoWithAuth(
                                filename
                            );
                            setLogoPreview(logoBlobUrl);
                            form.setFieldValue(
                                ClientFormFields.Logo,
                                editData.logo_url
                            );
                        } catch (error) {
                            const logoUrl = `${window.location.origin}/api/clients/logo/${filename}`;
                            setLogoPreview(logoUrl);
                            form.setFieldValue(
                                ClientFormFields.Logo,
                                editData.logo_url
                            );
                        }
                    }
                } else {
                    setLogoPreview(null);
                    setLogoFile(null);
                    form.setFieldValue(ClientFormFields.Logo, null);
                }
                if (editData.country) {
                    handleCountryChange(editData.country);
                }
                setClientStatus(editData.status);
                if (editData.subscription.plan_id) {
                    form.setFieldValue(
                        ClientFormFields.PlanId,
                        editData.subscription.plan_id
                    );
                } else if (
                    editData.subscription.plan_name &&
                    plans.length > 0
                ) {
                    const planId = plans.find(
                        (p) => p.name === editData.subscription.plan_name
                    )?.id;
                    if (planId) {
                        form.setFieldValue(ClientFormFields.PlanId, planId);
                    }
                }
            }
        };

        initializeForm();

        return () => {
            if (logoPreview && logoPreview.startsWith("blob:")) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [editData]);

    const loadInitialData = async () => {
        try {
            const [
                groupsResponse,
                plansResponse,
                countriesResponse,
                timezonesResponse,
            ] = await Promise.all([
                getClientGroups(),
                getAvailablePlans(),
                fetchCountriesData(),
                fetchTimezones(),
            ]);

            setClientGroups(groupsResponse);
            setPlans(plansResponse);
            setCountries(countriesResponse);
            setTimezones(timezonesResponse);
        } catch (error) {
            showToast("danger", "Failed to load initial data");
        }
    };

    const handleGroupChange = async (groupId: number) => {
        setSelectedGroup(groupId);
        setIsNewGroup(false);
        form.setFieldValue(ClientFormFields.IsNewGroup, false);
        try {
            const groupSubgroups = await getClientSubgroups(groupId);
            setSubgroups(groupSubgroups);
        } catch (error) {
            showToast("danger", "Failed to load subgroups");
        }
    };

    const handleCountryChange = async (countryCode: string) => {
        setSelectedCountry(countryCode);
        try {
            const availableChannels = await getAvailableChannels(countryCode);
            setChannels(availableChannels);
        } catch (error) {
            showToast("danger", "Failed to load channels");
        }
    };

    const handleSubgroupChange = (value: string) => {
        setIsNewSubgroup(value === "new");
    };

    const handleStatusChange = (checked: boolean) => {
        setClientStatus(checked);
    };

    const handleLogoUpload = (info: any) => {
        if (info.file.status === "uploading") {
            return;
        }
        if (info.file.status === "done") {
            const file = info.file.originFileObj;
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
            form.setFieldValue(ClientFormFields.Logo, file);
            message.success(`${info.file.name} file uploaded successfully`);
        }
        if (info.file.status === "error") {
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    const handleLogoRemove = () => {
        setLogoFile(null);
        setLogoPreview(null);
        form.setFieldValue(ClientFormFields.Logo, null);
        if (logoPreview && logoPreview.startsWith("blob:")) {
            URL.revokeObjectURL(logoPreview);
        }
    };

    const beforeLogoUpload = (file: File) => {
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
            message.error("You can only upload image files!");
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error("Image must be smaller than 5MB!");
            return false;
        }
        return true;
    };

    const handleSubmit = async (values: IClientFormValues) => {
        setSubmitLoading(true);
        try {
            const payload: ICreateClientPayload = {
                name: values[ClientFormFields.Name],
                plan_id: values[ClientFormFields.PlanId],
                country: values[ClientFormFields.Country],
                contact: values[ClientFormFields.Contact],
                status: values[ClientFormFields.Status],
                timezone: values[ClientFormFields.Timezone],
                client_group: {
                    id: values[ClientFormFields.IsNewGroup]
                        ? 0
                        : values[ClientFormFields.ClientGroupId],
                    name: values[ClientFormFields.IsNewGroup]
                        ? values[ClientFormFields.ClientGroupName]
                        : clientGroups.find(
                              (g) =>
                                  g.id ===
                                  values[ClientFormFields.ClientGroupId]
                          )?.name || "",
                },
                client_subgroup: {
                    id:
                        values[ClientFormFields.ClientSubgroupId] === "new"
                            ? 0
                            : values[ClientFormFields.ClientSubgroupId],
                    name:
                        values[ClientFormFields.ClientSubgroupId] === "new"
                            ? values[ClientFormFields.ClientSubgroupName]
                            : subgroups.find(
                                  (s) =>
                                      s.id ===
                                      values[ClientFormFields.ClientSubgroupId]
                              )?.name || "",
                },
                domain: values[ClientFormFields.Domain],
            };

            if (editData) {
                await updateClient(editData.id, payload);
                if (logoFile instanceof File) {
                    try {
                        await uploadClientLogo(editData.id, logoFile);
                    } catch (err) {
                        showToast("danger", "Failed to upload logo");
                        Sentry.captureException(err);
                    }
                }
                showToast("success", "Client updated successfully");
                navigate(
                    `/${ParentModule.ADMIN_SETTINGS}/settings/clients/${editData.id}`
                );
            } else {
                const response = await createClient(payload);
                if (!response?.data?.id) {
                    throw new Error("Failed to get new client ID from server");
                }
                if (logoFile instanceof File) {
                    try {
                        await uploadClientLogo(response.data.id, logoFile);
                    } catch (err) {
                        showToast("danger", "Failed to upload logo");
                        Sentry.captureException(err);
                    }
                }
                showToast("success", "Client created successfully");
                navigate(
                    `/${ParentModule.ADMIN_SETTINGS}/settings/clients/${response.data.id}`
                );
            }
        } catch (error: any) {
            showToast("danger", error.message || "Operation failed");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!editData?.id) return;

        setDeleteLoading(true);
        try {
            await deleteClient(editData.id);
            showToast("success", "Client deleted successfully");
            onSuccess();
        } catch (error: any) {
            showToast("danger", error.message || "Failed to delete client");
        } finally {
            setDeleteLoading(false);
        }
    };

    const filterOption = (input: string, option: CustomOptionType) =>
        option?.children
            ?.toString()
            .toLowerCase()
            .indexOf(input.toLowerCase()) >= 0;

    // Add validation functions
    const validateGroupName = (name: string) => {
        const exists = metadata?.groups.some(
            (group) => group.name.toLowerCase() === name.toLowerCase()
        );
        if (exists) {
            return Promise.reject(
                new Error("A group with this name already exists")
            );
        }
        return Promise.resolve();
    };

    const validateSubgroupName = (name: string) => {
        const exists = metadata?.subgroups.some(
            (subgroup) => subgroup.name.toLowerCase() === name.toLowerCase()
        );
        if (exists) {
            return Promise.reject(
                new Error("A subgroup with this name already exists")
            );
        }
        return Promise.resolve();
    };

    return (
        <>
            <Row className="">
                <Col span={12}>
                    <h5>{editData ? "Edit Client" : "Create Client"}</h5>
                </Col>
                <Col span={12} className="text-end">
                    <Button type="primary" className="ms-3" onClick={onCancel}>
                        Manage Clients
                    </Button>
                </Col>
            </Row>

            <div
                className="mt-4"
                style={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    height: "75vh",
                    paddingBottom: "50px",
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ [ClientFormFields.Status]: true }}
                >
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                label="Client Name"
                                name={ClientFormFields.Name}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter client name",
                                    },
                                ]}
                            >
                                <Input
                                    className="border"
                                    placeholder="Enter Client Name"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Domain"
                                name={ClientFormFields.Domain}
                            >
                                <Input
                                    className="border"
                                    placeholder="Enter Domain (optional)"
                                />
                            </Form.Item>

                            <Form.Item
                                label={
                                    isNewGroup
                                        ? "Client Group Name"
                                        : "Select Client Group"
                                }
                                name={
                                    isNewGroup
                                        ? ClientFormFields.ClientGroupName
                                        : ClientFormFields.ClientGroupId
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: `Please ${
                                            isNewGroup
                                                ? "enter group name"
                                                : "select a group"
                                        }`,
                                    },
                                    isNewGroup && {
                                        validator: async (
                                            _: unknown,
                                            value: string
                                        ) => {
                                            if (value) {
                                                await validateGroupName(value);
                                            }
                                        },
                                    },
                                ].filter(Boolean)}
                            >
                                {isNewGroup ? (
                                    <Input
                                        className="border"
                                        placeholder="Enter Group Name"
                                    />
                                ) : (
                                    <Select
                                        placeholder="Select Client Group"
                                        onChange={handleGroupChange}
                                        showSearch
                                        filterOption={filterOption}
                                    >
                                        {clientGroups.map((group) => (
                                            <Option
                                                key={group.id}
                                                value={group.id}
                                            >
                                                {group.name}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>

                            <Form.Item
                                label="Plan Selection"
                                name={ClientFormFields.PlanId}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a plan",
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select Plan"
                                    showSearch
                                    filterOption={filterOption}
                                >
                                    {plans.map((plan) => (
                                        <Option key={plan.id} value={plan.id}>
                                            {plan.name} - {plan.tier} ($
                                            {plan.price} / {plan.duration})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Country"
                                name={ClientFormFields.Country}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a country",
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select Country"
                                    onChange={handleCountryChange}
                                    value={selectedCountry}
                                    showSearch
                                    filterOption={filterOption}
                                >
                                    {countries.map((country) => (
                                        <Option
                                            key={country.alpha2_code}
                                            value={country.alpha2_code}
                                        >
                                            {country.name} (
                                            {country.alpha2_code})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Contact Name"
                                name={[ClientFormFields.Contact, "name"]}
                            >
                                <Input
                                    className="border"
                                    placeholder="Enter Contact Name"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Status"
                                name={ClientFormFields.Status}
                                valuePropName="checked"
                            >
                                <Switch onChange={handleStatusChange} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Is New Client Group?"
                                name={ClientFormFields.IsNewGroup}
                                hidden={!isCreating}
                            >
                                <Radio.Group
                                    onChange={(e) =>
                                        setIsNewGroup(e.target.value)
                                    }
                                    value={isNewGroup}
                                >
                                    <Radio value={true}>Yes</Radio>
                                    <Radio value={false}>No</Radio>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item
                                label="Select Subgroup"
                                name={ClientFormFields.ClientSubgroupId}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select subgroup",
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select Subgroup"
                                    disabled={!selectedGroup && !isNewGroup}
                                    showSearch
                                    filterOption={filterOption}
                                    onChange={handleSubgroupChange}
                                >
                                    {isCreating && (
                                        <Option value="new">
                                            New Subgroup
                                        </Option>
                                    )}
                                    {subgroups.map((subgroup) => (
                                        <Option
                                            key={subgroup.id}
                                            value={subgroup.id}
                                        >
                                            {subgroup.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {isNewSubgroup && (
                                <Form.Item
                                    label="New Subgroup Name"
                                    name={ClientFormFields.ClientSubgroupName}
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please enter subgroup name",
                                        },
                                        {
                                            validator: async (
                                                _: unknown,
                                                value: string
                                            ) => {
                                                if (value) {
                                                    await validateSubgroupName(
                                                        value
                                                    );
                                                }
                                            },
                                        },
                                    ]}
                                >
                                    <Input
                                        className="border"
                                        placeholder="Enter Subgroup Name"
                                    />
                                </Form.Item>
                            )}

                            <Form.Item
                                label="Timezone"
                                name={ClientFormFields.Timezone}
                            >
                                <Select
                                    placeholder="Select Timezone"
                                    showSearch
                                    filterOption={filterOption}
                                >
                                    {timezones.map((timezone) => (
                                        <Option
                                            key={timezone.label}
                                            value={timezone.label}
                                        >
                                            {timezone.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Contact Email"
                                name={[ClientFormFields.Contact, "email"]}
                                rules={[
                                    {
                                        type: "email",
                                        message: "Please enter a valid email",
                                    },
                                ]}
                            >
                                <Input
                                    className="border"
                                    placeholder="Enter Contact Email"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Contact Phone"
                                name={[ClientFormFields.Contact, "phone"]}
                            >
                                <Input
                                    className="border"
                                    placeholder="Enter Contact Phone"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Client Logo Section */}
                    <Row gutter={24} className="mt-4">
                        <Col span={24}>
                            <Form.Item
                                label="Client Logo"
                                name={ClientFormFields.Logo}
                            >
                                <Upload
                                    name="logo"
                                    listType="picture-card"
                                    className="logo-uploader"
                                    showUploadList={false}
                                    beforeUpload={beforeLogoUpload}
                                    onChange={handleLogoUpload}
                                    customRequest={({ file, onSuccess }) => {
                                        setTimeout(() => {
                                            onSuccess?.(file);
                                        }, 0);
                                    }}
                                >
                                    {logoPreview ? (
                                        <div className="logo-preview-container">
                                            <img src={logoPreview} alt="logo" />
                                            <Button
                                                type="text"
                                                size="small"
                                                className="logo-remove-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLogoRemove();
                                                }}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="upload-placeholder">
                                            <UploadOutlined />
                                            <div className="upload-text">
                                                Upload Logo
                                            </div>
                                        </div>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16} className="mt-4">
                        <Col>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitLoading}
                                disabled={editData && !canUpdateClient}
                            >
                                {editData ? "Update" : "Create"} Client
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                onClick={onCancel}
                                style={{
                                    backgroundColor: "#6c757d",
                                    color: "white",
                                }}
                            >
                                Cancel
                            </Button>
                        </Col>
                        {editData && canDeleteClient && (
                            <Col>
                                <Popconfirm
                                    title="Delete Client"
                                    description="Are you sure you want to delete this client?"
                                    onConfirm={handleDelete}
                                    okText="Yes"
                                    cancelText="No"
                                    okButtonProps={{
                                        danger: true,
                                        className: "ms-2",
                                    }}
                                    overlayClassName="p-3"
                                    styles={{ body: { padding: "12px" } }}
                                >
                                    <Button
                                        danger
                                        loading={deleteLoading}
                                        disabled={editData.is_archived}
                                    >
                                        Delete
                                    </Button>
                                </Popconfirm>
                            </Col>
                        )}
                    </Row>
                </Form>
            </div>
        </>
    );
};

export default ClientForm;
